import {
  Flex,
  IconButton,
  Box,
  Text,
  Badge,
  Divider,
  SystemStyleObject,
} from '@chakra-ui/react';
import { mdiClose } from '@mdi/js';
import {
  chakraComponents,
  components,
  GroupBase,
  OptionProps,
  Select as ReactSelect,
} from 'chakra-react-select';
import { ResourceByProductType } from 'features/context-ids/types/types';
import { ImageWithSkeleton } from '../../ImageWithSkeleton';
import { PortalIcon } from '../../PortalIcon';

type Item = {
  label: string;
  value: string;
  productCode: string;
  environment: string;
  showHeader: boolean;
};

const chakraStyles = {
  menuList: (provided: SystemStyleObject) => ({
    ...provided,
    p: 0,
  }),
};

export const SelectedProductRow = ({
  productCode,
  name,
  iconUrl,
  handleTenantSelect,
  removeProductHandler,
  tenants,
  index,
}: {
  productCode: string;
  name: string;
  iconUrl: string;
  handleTenantSelect: (productCode: string, tenantIdWithEnv: string) => void;
  removeProductHandler: (index: number) => void;
  tenants: ResourceByProductType[];
  index: number;
}) => (
  <Flex
    key={productCode}
    justifyContent='space-between'
    mb={4}
    data-testid={`selected-product-${productCode}`}
  >
    <Flex
      gap={4}
      minW='40'
      direction='column'
      justifyContent='start'
      alignItems='start'
    >
      {index === 0 && (
        <Text variant='subtle' fontWeight='semibold'>
          Resource
        </Text>
      )}
      <Flex alignItems='center' gap={2} mt={1}>
        <ImageWithSkeleton
          key={name}
          width='7'
          height='7'
          src={iconUrl}
          alt={name}
          flexShrink={0}
          data-testid={`product-icon-${productCode}`}
        />
        <Text data-testid={`product-name-${productCode}`}>{name}</Text>
      </Flex>
    </Flex>
    <Flex
      gap={4}
      flex={1}
      px={8}
      direction='column'
      alignItems='start'
      justifyContent='start'
    >
      {index === 0 && (
        <Text variant='subtle' fontWeight='semibold'>
          Tenant
        </Text>
      )}
      <Box w='full'>
        <ReactSelect
          maxMenuHeight={274}
          components={{ ...chakraComponents, Option: CustomOption }}
          selectedOptionColorScheme='primary'
          chakraStyles={chakraStyles}
          placeholder='Select'
          options={tenants.map(
            ({
              tenantName,
              tenantId,
              productCode,
              environment,
              showHeader,
            }: ResourceByProductType) => ({
              label: tenantName,
              // tenantId is not unique for live - preview. Need to construct a unique value
              value: `${tenantId} ${environment ?? 'non-env'}`,
              productCode,
              environment,
              showHeader,
            }),
          )}
          onChange={(data) => {
            const item = data as Item;
            if (item) handleTenantSelect(productCode, item.value);
          }}
          data-testid={`tenant-select-${productCode}`}
        />
      </Box>
    </Flex>
    <IconButton
      alignSelf='end'
      mb={1}
      variant='ghost'
      size='sm'
      aria-label='remove product'
      icon={<PortalIcon path={mdiClose} />}
      onClick={() => removeProductHandler(index)}
      data-testid={`remove-product-${index}`}
    />
  </Flex>
);

export const CustomOption = (
  props: JSX.IntrinsicAttributes &
    OptionProps<unknown, boolean, GroupBase<unknown>>,
) => {
  const item = props.data as Item;

  if (item.environment) {
    const [projectName, environmentName] = item.label.split(' | ');
    //!item.showHeader means it is last item on list
    return (
      <>
        {item.showHeader && (
          <Text
            px={4}
            py={2}
            pt={4}
            textTransform='uppercase'
            variant='subtle'
            fontWeight='bold'
          >
            {projectName}
          </Text>
        )}
        <components.Option {...props}>
          <Flex
            justifyContent='space-between'
            alignItems='center'
            cursor='pointer'
          >
            <Text px={2} py={1}>
              {environmentName}
            </Text>
            <Badge
              textTransform='capitalize'
              colorScheme={
                item.environment === 'Preview' ? 'neutral' : 'success'
              }
            >
              {item.environment}
            </Badge>
          </Flex>
        </components.Option>

        {!item.showHeader && <Divider />}
      </>
    );
  }

  return (
    <components.Option {...props}>
      <Flex justifyContent='space-between' alignItems='center' cursor='pointer'>
        <span>{item.label}</span>
      </Flex>
    </components.Option>
  );
};
